import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API from '../../../context/API';
import { Device, DeviceType, Merchant, Store } from '../types';

export interface DevicesState {
  devices: Device[];
  deviceTypes: DeviceType[];
  merchants: Merchant[];
  stores: Store[];
  filteredDevices: Device[];
  loading: {
    devices: boolean;
    deviceTypes: boolean;
    merchants: boolean;
    stores: boolean;
    create: boolean;
    update: boolean;
    delete: boolean;
  };
  error: string | null;
}

const initialState: DevicesState = {
  devices: [],
  deviceTypes: [],
  merchants: [],
  stores: [],
  filteredDevices: [],
  loading: {
    devices: false,
    deviceTypes: false,
    merchants: false,
    stores: false,
    create: false,
    update: false,
    delete: false,
  },
  error: null,
};

// Fetch devices
export const fetchDevices = createAsyncThunk<
  Device[],
  void,
  { rejectValue: string }
>('devices/fetchDevices', async (_, { rejectWithValue }) => {
  try {
    const response = await API.get('devices', {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: false,
    });
    return response.data.data || [];
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to fetch devices'
    );
  }
});

// Fetch device types
export const fetchDeviceTypes = createAsyncThunk<
  DeviceType[],
  void,
  { rejectValue: string }
>('devices/fetchDeviceTypes', async (_, { rejectWithValue }) => {
  try {
    const response = await API.get('device-types', {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: false,
    });
    return response.data.data || [];
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to fetch device types'
    );
  }
});

// Fetch merchants
export const fetchMerchants = createAsyncThunk<
  Merchant[],
  void,
  { rejectValue: string }
>('devices/fetchMerchants', async (_, { rejectWithValue }) => {
  try {
    const response = await API.get('merchant', {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: false,
    });
    return response.data.data || [];
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to fetch merchants'
    );
  }
});

// Fetch stores by merchant
export const fetchStoresByMerchant = createAsyncThunk<
  Store[],
  string,
  { rejectValue: string }
>('devices/fetchStoresByMerchant', async (merchantId, { rejectWithValue }) => {
  try {
    const response = await API.get(`/stores/by-merchant/${merchantId}`, {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: false,
    });
    return response.data.data || [];
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to fetch stores'
    );
  }
});

// Create device
export const createDevice = createAsyncThunk<
  Device,
  Partial<Device>,
  { rejectValue: string }
>('devices/createDevice', async (device, { rejectWithValue }) => {
  try {
    const response = await API.post('devices', device, {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: false,
    });
    if (response.data.status === 200) {
      return response.data.data;
    }
    return rejectWithValue(response.data.message || 'Failed to create device');
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to create device'
    );
  }
});

// Update device
export const updateDevice = createAsyncThunk<
  Device,
  { id: string; data: Partial<Device> },
  { rejectValue: string }
>('devices/updateDevice', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await API.put(`devices/${id}`, data, {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: false,
    });
    if (response.data.status === 200) {
      return response.data.data;
    }
    return rejectWithValue(response.data.message || 'Failed to update device');
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to update device'
    );
  }
});

// Delete device
export const deleteDevice = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>('devices/deleteDevice', async (id, { rejectWithValue }) => {
  try {
    const response = await API.delete(`devices/${id}`, {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: false,
    });
    if (response.data.status === 200) {
      return id;
    }
    return rejectWithValue(response.data.message || 'Failed to delete device');
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to delete device'
    );
  }
});

const devicesSlice = createSlice({
  name: 'devices',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearDevices: (state) => {
      state.devices = [];
      state.filteredDevices = [];
    },
    setFilteredDevices: (state, action) => {
      state.filteredDevices = action.payload;
    },
    clearStores: (state) => {
      state.stores = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch devices
      .addCase(fetchDevices.pending, (state) => {
        state.loading.devices = true;
        state.error = null;
      })
      .addCase(fetchDevices.fulfilled, (state, action) => {
        state.loading.devices = false;
        state.devices = action.payload;
        state.filteredDevices = action.payload;
      })
      .addCase(fetchDevices.rejected, (state, action) => {
        state.loading.devices = false;
        state.error = action.payload || 'Failed to fetch devices';
      })
      // Fetch device types
      .addCase(fetchDeviceTypes.pending, (state) => {
        state.loading.deviceTypes = true;
        state.error = null;
      })
      .addCase(fetchDeviceTypes.fulfilled, (state, action) => {
        state.loading.deviceTypes = false;
        state.deviceTypes = action.payload;
      })
      .addCase(fetchDeviceTypes.rejected, (state, action) => {
        state.loading.deviceTypes = false;
        state.error = action.payload || 'Failed to fetch device types';
      })
      // Fetch merchants
      .addCase(fetchMerchants.pending, (state) => {
        state.loading.merchants = true;
        state.error = null;
      })
      .addCase(fetchMerchants.fulfilled, (state, action) => {
        state.loading.merchants = false;
        state.merchants = action.payload;
      })
      .addCase(fetchMerchants.rejected, (state, action) => {
        state.loading.merchants = false;
        state.error = action.payload || 'Failed to fetch merchants';
      })
      // Fetch stores by merchant
      .addCase(fetchStoresByMerchant.pending, (state) => {
        state.loading.stores = true;
        state.error = null;
      })
      .addCase(fetchStoresByMerchant.fulfilled, (state, action) => {
        state.loading.stores = false;
        state.stores = action.payload;
      })
      .addCase(fetchStoresByMerchant.rejected, (state, action) => {
        state.loading.stores = false;
        state.error = action.payload || 'Failed to fetch stores';
      })
      // Create device
      .addCase(createDevice.pending, (state) => {
        state.loading.create = true;
        state.error = null;
      })
      .addCase(createDevice.fulfilled, (state, action) => {
        state.loading.create = false;
        state.devices.push(action.payload);
        state.filteredDevices.push(action.payload);
      })
      .addCase(createDevice.rejected, (state, action) => {
        state.loading.create = false;
        state.error = action.payload || 'Failed to create device';
      })
      // Update device
      .addCase(updateDevice.pending, (state) => {
        state.loading.update = true;
        state.error = null;
      })
      .addCase(updateDevice.fulfilled, (state, action) => {
        state.loading.update = false;
        const index = state.devices.findIndex((d) => d.id === action.payload.id);
        if (index !== -1) {
          state.devices[index] = action.payload;
          const filteredIndex = state.filteredDevices.findIndex((d) => d.id === action.payload.id);
          if (filteredIndex !== -1) {
            state.filteredDevices[filteredIndex] = action.payload;
          }
        }
      })
      .addCase(updateDevice.rejected, (state, action) => {
        state.loading.update = false;
        state.error = action.payload || 'Failed to update device';
      })
      // Delete device
      .addCase(deleteDevice.pending, (state) => {
        state.loading.delete = true;
        state.error = null;
      })
      .addCase(deleteDevice.fulfilled, (state, action) => {
        state.loading.delete = false;
        state.devices = state.devices.filter((d) => d.id !== action.payload);
        state.filteredDevices = state.filteredDevices.filter((d) => d.id !== action.payload);
      })
      .addCase(deleteDevice.rejected, (state, action) => {
        state.loading.delete = false;
        state.error = action.payload || 'Failed to delete device';
      });
  },
});

export const { clearError, clearDevices, setFilteredDevices, clearStores } = devicesSlice.actions;
export default devicesSlice.reducer;
